import React from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { loginRequest, entraApiScope } from './msal.ts';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { setCreatorAuthHeader } from '../services/api.ts';
import { getApiAccessToken } from './accessToken.ts';
import { apiV1Url } from '../lib/apiBaseUrl.ts';
import {
  clearCreatorSessionToken,
  getStoredCreatorSessionToken,
  storeCreatorSessionToken
} from './sessionToken.ts';

type Props = {
  children: React.ReactNode;
};

export function AuthGate({ children }: Props) {
  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress } = useMsal();
  const hasDashboardSsoCode = React.useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('dashboardSso') === 'maker' && !!params.get('code');
  }, []);
  const [dashboardSessionReady, setDashboardSessionReady] = React.useState(() => {
    if (hasDashboardSsoCode) return false;
    return Boolean(getStoredCreatorSessionToken());
  });
  const [dashboardBootstrapBusy, setDashboardBootstrapBusy] = React.useState(hasDashboardSsoCode);
  const [dashboardBootstrapError, setDashboardBootstrapError] = React.useState<string | null>(null);
  const [tokenReady, setTokenReady] = React.useState(false);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = (params.get('code') || '').trim();
    const isDashboardLaunch = params.get('dashboardSso') === 'maker';

    if (!isDashboardLaunch || !code) {
      const storedToken = getStoredCreatorSessionToken();
      if (storedToken) {
        setCreatorAuthHeader(`Bearer ${storedToken}`);
        setDashboardSessionReady(true);
      } else {
        setDashboardSessionReady(false);
      }
      setDashboardBootstrapBusy(false);
      return;
    }

    let cancelled = false;
    setDashboardBootstrapBusy(true);
    setDashboardBootstrapError(null);
    clearCreatorSessionToken();
    setDashboardSessionReady(false);
    setCreatorAuthHeader(null);

    (async () => {
      try {
        const response = await fetch(apiV1Url('/api/v1/auth/dashboard-sso'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ code })
        });
        if (!response.ok) {
          throw new Error(await response.text() || 'Failed to consume dashboard launch code');
        }
        const payload = await response.json() as { token?: unknown };
        const token = typeof payload?.token === 'string' ? payload.token.trim() : '';
        if (!token) {
          throw new Error('Dashboard launch did not return a usable token');
        }
        storeCreatorSessionToken(token);
        setCreatorAuthHeader(`Bearer ${token}`);

        const nextUrl = new URL(window.location.href);
        nextUrl.searchParams.delete('code');
        nextUrl.searchParams.delete('dashboardSso');
        const nextHref = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
        window.history.replaceState(null, '', nextHref);

        if (!cancelled) {
          setDashboardSessionReady(true);
        }
      } catch (error) {
        clearCreatorSessionToken();
        setCreatorAuthHeader(null);
        if (!cancelled) {
          setDashboardBootstrapError(error instanceof Error ? error.message : 'Failed to prepare Dashboard session');
          setDashboardSessionReady(false);
        }
      } finally {
        if (!cancelled) {
          setDashboardBootstrapBusy(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // After redirect login, MSAL will land on /auth/callback. Once processing is done, move to /.
  React.useEffect(() => {
    if (window.location.pathname !== '/auth/callback') return;
    if (inProgress !== 'none') return;
    // If login succeeded, there should be an active account now; either way, return to app root.
    window.history.replaceState(null, '', '/');
  }, [inProgress]);

  // Ensure we can acquire an access token for the backend API scope before rendering the app.
  React.useEffect(() => {
    if (dashboardSessionReady || dashboardBootstrapBusy) return;
    if (!isAuthenticated) {
      setTokenReady(false);
      return;
    }
    if (inProgress !== 'none') return;
    if (window.location.pathname === '/auth/callback') return;

    let cancelled = false;
    (async () => {
      const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
      if (!account) {
        if (!cancelled) setTokenReady(false);
        return;
      }

      try {
        const token = await getApiAccessToken();
        setCreatorAuthHeader(`Bearer ${token}`);
        if (!cancelled) setTokenReady(true);
      } catch (e) {
        if (e instanceof InteractionRequiredAuthError) {
          // Trigger incremental consent / token issuance for API scope.
          instance.acquireTokenRedirect({ scopes: [entraApiScope] });
          return;
        }
        setCreatorAuthHeader(null);
        if (!cancelled) setTokenReady(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dashboardBootstrapBusy, dashboardSessionReady, isAuthenticated, inProgress, instance]);

  if (dashboardBootstrapBusy) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center">
          <div className="text-lg font-semibold">Signing you in…</div>
          <div className="text-sm text-gray-600 mt-1">Preparing your Dashboard launch session.</div>
        </div>
      </div>
    );
  }

  if (dashboardSessionReady) return <>{children}</>;

  if (window.location.pathname === '/auth/callback') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center">
          <div className="text-lg font-semibold">Signing you in…</div>
          <div className="text-sm text-gray-600 mt-1">Completing authentication redirect.</div>
        </div>
      </div>
    );
  }

  if (isAuthenticated && tokenReady) return <>{children}</>;
  if (isAuthenticated && !tokenReady) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center">
          <div className="text-lg font-semibold">Preparing your session…</div>
          <div className="text-sm text-gray-600 mt-1">Acquiring API access token.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white rounded-lg border p-6 text-center space-y-3">
        <div className="text-xl font-semibold">Sign in required</div>
        <div className="text-sm text-gray-600">
          Please sign in to analyze documentation and create SIMPIs.
        </div>
        {dashboardBootstrapError && (
          <div className="text-sm text-amber-700">
            {dashboardBootstrapError}
          </div>
        )}
        <button
          onClick={() => instance.loginRedirect(loginRequest)}
          className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          Sign in
        </button>
      </div>
    </div>
  );
}

